import {
  createAuthorizationHeaders,
  observableFetchClient,
} from '@vp/common-api-utils';
import { getAuthHeaderAndShopperId } from '@vp/common-api-utils/authHelpers';
import qs from 'qs';
import type { FeedbackGroup, FeedbackType } from '../types/FeedbackGroup.types';

const prodOrcaBaseUrl = 'https://orca.orders.vpsvc.com';
const stagingOrcaBaseUrl = 'https://orca-staging.orders.vpsvc.com';

/**
 * Gets the base url for ORCA service
 * In the future, consider adding timeouts as part of the configuration.
 */
const getConfigForEnvironment = (environment: string): string => {
  if (!environment) {
    console.warn(
      `Unable to derive service config for a null/empty environment, using STAGING environment as sensible fallback.`
    );
    return stagingOrcaBaseUrl;
  }
  switch (environment) {
    case 'PRODUCTION':
      return prodOrcaBaseUrl;
    case 'STAGING':
      return stagingOrcaBaseUrl;
    default:
      console.warn(
        `Unsupported environment type of ${environment}, using STAGING environment as sensible fallback.`
      );
      return stagingOrcaBaseUrl;
  }
};

/**
 * Fetch the feedback groups awaiting feedback for the given order
 */
type GetFeedbackGroupsAwaitingFeedbackParams = {
  orderNumber: string;
  shopperId: string;
  types: FeedbackType[];
  environment: string;
};

export const getFeedbackGroupsAwaitingFeedback = async ({
  orderNumber,
  shopperId,
  types,
  environment,
}: GetFeedbackGroupsAwaitingFeedbackParams): Promise<FeedbackGroup[]> => {
  const baseUrl = getConfigForEnvironment(environment);
  const searchParams = qs.stringify(
    { states: ['AWAITING_FEEDBACK'], types },
    { arrayFormat: 'repeat' }
  );
  const url = `${baseUrl}/api/v1/shoppers/${shopperId}/orders/${orderNumber}/feedbackGroups?${searchParams}`;

  const { authHeader } = getAuthHeaderAndShopperId();
  try {
    const response = await observableFetchClient({
      url: url,
      method: 'get',
      headers: createAuthorizationHeaders(authHeader),
    });
    const feedbackGroups = (await response.json()) as {
      feedbackGroups: FeedbackGroup[];
    };
    return feedbackGroups.feedbackGroups;
  } catch (_) {
    // silently handling error for now
    return [];
  }
};
